import { AlertCircle, LinkIcon } from 'lucide-react';
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router';
import { errorMessage, isApiError } from '@/api/client';
import { MIN_PASSWORD_LENGTH, useAcceptInvite, useInviteInfo } from '@/api/hooks/auth';
import { Button, EmptyState, Field, Input, LoadingBlock } from '@/components';
import { paths } from '@/routing';
import { AuthLayout } from './AuthLayout';
import styles from './forms.module.css';

/**
 * The invite token travels in the URL fragment (`/invite#<token>`): fragments are never
 * sent to the server, so the token stays out of access logs and `Referer` headers. Old
 * `/invite/<token>` links still work; the token is moved into the fragment at once.
 */
function useInviteToken(): string {
  const pathToken = useParams().token;
  const location = useLocation();
  const navigate = useNavigate();
  const token = pathToken ?? decodeURIComponent(location.hash.replace(/^#/, ''));
  useEffect(() => {
    if (pathToken) void navigate({ pathname: '/invite', hash: pathToken }, { replace: true });
  }, [navigate, pathToken]);
  return token;
}

export default function AcceptInvite() {
  const token = useInviteToken();
  const invite = useInviteInfo(token);
  const accept = useAcceptInvite();
  const navigate = useNavigate();
  const [name, setName] = useState<string | null>(null);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [submitted, setSubmitted] = useState(false);

  if (token && invite.isPending) return <LoadingBlock />;
  if (!token || invite.isError || !invite.data?.valid) {
    const notFound = !token || (invite.isError && isApiError(invite.error) && invite.error.status === 404);
    return (
      <AuthLayout title="Invite link">
        <EmptyState
          compact
          icon={<LinkIcon />}
          title={
            notFound || invite.isSuccess
              ? 'This invite link is no longer valid'
              : 'Could not check the invite'
          }
          description={
            invite.isError && !notFound
              ? errorMessage(invite.error)
              : 'It was already used, has expired (links are valid for 7 days) or was replaced. Ask an administrator for a new one.'
          }
          action={<Link to={paths.login()}>Go to sign in</Link>}
        />
      </AuthLayout>
    );
  }

  const displayName = name ?? invite.data.name;
  const passwordError =
    password.length < MIN_PASSWORD_LENGTH ? `At least ${MIN_PASSWORD_LENGTH} characters` : undefined;
  const confirmError = confirmation !== password ? 'Passwords do not match' : undefined;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    setSubmitted(true);
    if (passwordError || confirmError) return;
    accept.mutate(
      { token, password, name: displayName.trim() || null },
      { onSuccess: () => void navigate(paths.home(), { replace: true }) },
    );
  };

  return (
    <AuthLayout
      title="Set up your account"
      subtitle={
        <>
          You were invited as <strong>{invite.data.email}</strong>.
        </>
      }
    >
      <form className={styles.form} onSubmit={onSubmit} noValidate>
        {accept.isError && (
          <div className={styles.alert} role="alert">
            <AlertCircle aria-hidden />
            <span>{errorMessage(accept.error)}</span>
          </div>
        )}
        <Field label="Name">
          <Input autoComplete="name" value={displayName} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field
          label="Password"
          help={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          error={submitted ? passwordError : undefined}
          required
        >
          <Input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
        </Field>
        <Field label="Confirm password" error={submitted ? confirmError : undefined} required>
          <Input
            type="password"
            autoComplete="new-password"
            value={confirmation}
            onChange={(e) => setConfirmation(e.target.value)}
          />
        </Field>
        <Button type="submit" variant="primary" loading={accept.isPending}>
          Create account
        </Button>
      </form>
    </AuthLayout>
  );
}
